import React, { useState } from "react";
import {
  Box,
  Typography,
  styled,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import Packages from "./packages";
import PromoCodes from "./promo-codes";
import Images from "./images";
import ContactNumber from "./contact-no";
import Franchise from "./franchise";
import CareerAppications from "./career-appications";
import CheckConsent from "./check-consent";
import Birthday from "./birthday";
import TokenManagement from "./token";
import CustomerDataTable from "./customerData";
import EventManagement from "./eventManagment";

const SidebarItem = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: "12px",
  margin: "6px 8px",
  backgroundColor: active ? "rgba(255, 255, 255, 0.15)" : "transparent",
  border: active
    ? "1px solid rgba(255, 255, 255, 0.25)"
    : "1px solid transparent",
  boxShadow: active ? "0 2px 8px rgba(0, 0, 0, 0.08)" : "none",
  padding: "12px 16px",
  minHeight: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: active
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(255, 255, 255, 0.1)",
    transform: "translateX(4px)",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
  },
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  "& .MuiListItemText-primary": {
    fontWeight: active ? "600" : "500",
    color: active ? "white" : "rgba(255, 255, 255, 0.9)",
    fontSize: "15px",
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    boxShadow: "none",
    transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflowX: "hidden",
  },
}));

const AdminPortal = () => {
  const [active, setActive] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const navItems = [
    "Packages",
    "Promo Codes",
    "Images",
    "CustomerData",
    "Contact Numbers",
    "Franchises",
    "Career Applications",
    "Birthday",
    "Token",
    "Event",
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (index) => {
    setActive(index);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Function to get shortened text for collapsed sidebar
  const getCollapsedText = (text) => {
    const words = text.split(" ");
    if (words.length === 1) {
      // Single word: take first 2 letters
      return text.substring(0, 2).toUpperCase();
    } else {
      // Multiple words: take first letter of first 2 words
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
  };

  const getActivePage = () => {
    switch (active) {
      case 0:
        return <Packages />;
      case 1:
        return <PromoCodes />;
      case 2:
        return <Images />;
      case 3:
        return <CustomerDataTable />;
      case 4:
        return <ContactNumber />;
      case 5:
        return <Franchise />;
      case 6:
        return <CareerAppications />;
      case 7:
        return <Birthday />;
      case 8:
        return <TokenManagement />;
      case 9:
        return <EventManagement />;
      default:
        return <Packages />;
    }
  };

  const sidebarContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarCollapsed ? "center" : "space-between",
          p: 3,
          pb: 2,
          minHeight: "72px",
        }}
      >
        {!sidebarCollapsed && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: "700",
              color: "white",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            Admin Portal
          </Typography>
        )}
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        )}
        {!isMobile && (
          <IconButton
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            sx={{
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <CloseIcon />}
          </IconButton>
        )}
      </Box>

      <Divider
        sx={{
          borderColor: "rgba(255, 255, 255, 0.2)",
          mx: 3,
          mb: 2,
        }}
      />

      {/* Navigation Items */}
      <Box
        sx={{ px: sidebarCollapsed ? 1 : 2, flexGrow: 1, overflowY: "auto" }}
      >
        <List sx={{ padding: 0 }}>
          {navItems.map((item, index) => (
            <ListItem key={index} disablePadding>
              <SidebarItem
                active={active === index}
                onClick={() => handleNavClick(index)}
                sx={{
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <ListItemText
                  primary={sidebarCollapsed ? getCollapsedText(item) : item}
                  sx={{
                    textAlign: "center",
                    margin: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "& .MuiListItemText-primary": {
                      color:
                        active === index ? "white" : "rgba(255, 255, 255, 0.9)",
                      fontWeight: active === index ? "600" : "500",
                      fontSize: sidebarCollapsed ? "16px" : "15px",
                      lineHeight: 1,
                      letterSpacing: sidebarCollapsed ? "1px" : "normal",
                    },
                  }}
                />
              </SidebarItem>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        display: "flex",
        position: "relative",
      }}
    >
      {/* Mobile Header with Hamburger */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: "80px",
            left: 0,
            right: 0,
            backgroundColor: "white",
            borderBottom: "1px solid #e5e7eb",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            zIndex: 100,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: "#667eea" }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: "600", color: "#1f2937" }}>
            {navItems[active]}
          </Typography>
        </Box>
      )}

      {/* Sidebar Drawer */}
      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block" },
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            position: isMobile ? "fixed" : "relative",
            height: "100vh",
            zIndex: isMobile ? 1300 : "auto",
            width: sidebarCollapsed ? 80 : 280,
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: 0,
            boxSizing: "border-box",
          },
        }}
      >
        {sidebarContent}
      </StyledDrawer>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: "#f8fafc",
        }}
      >
        {/* Content Area */}
        <Box
          sx={{
            margin: isMobile ? "0" : "0",
            marginTop: isMobile ? "150px" : "0px",
            backgroundColor: "white",
            borderRadius: 0,
            boxShadow: "none",
            height: isMobile ? "calc(100vh - 150px)" : "100vh",
            overflowY: "auto",
            overflowX: "hidden",
            flexGrow: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f5f9",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#cbd5e1",
              borderRadius: "10px",
              "&:hover": {
                background: "#94a3b8",
              },
            },
          }}
        >
          {getActivePage()}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPortal;
